import { sparklineColors } from '../../chart-colors.js';

/**
 * Sparkline — the trend line a stat tile carries beside its headline number.
 *
 * No corpus provenance for the mark: no design file draws a trend line. Two
 * neighbours are real. The tile it sits in is `DB:476-483`, a
 * KPI grid of a number and a label and nothing else; and the corpus's one
 * wordless data mark, the deadline meter at `DB:581`, names
 * itself the way this does — `role="img"` and a caller-supplied `aria-label`.
 * The line itself is the dataviz spec's.
 *
 * The description is a prop, not a literal: every string a person reads comes
 * from the caller's translation catalogue.
 */
export interface SparklineProps {
  readonly points: readonly number[];
  /** Accessible description of the trend, e.g. "12-week trend". */
  readonly label: string;
}

/**
 * 12-point stat-tile sparkline per the dataviz spec: 2px line in the
 * de-emphasis gray, current period as an accent end-dot with a 2px surface
 * ring. Decorative relative to the tile's value — described via aria-label.
 */
/**
 * The most points this will plot.
 *
 * `Math.min(...points)` spreads the series into an argument list, and an
 * argument list has a size limit — a long enough array throws a RangeError and
 * takes the whole dashboard render with it rather than losing one chart. The
 * series arrives from a query, so its length is whatever the query returned.
 * 96 is eight years of monthly points against a mark 120px wide; past that the
 * line has more points than pixels and the excess is not information.
 */
const MAX_POINTS = 96;

export function Sparkline({ points, label }: SparklineProps) {
  const width = 120;
  const height = 32;
  const pad = 4;
  // A single NaN propagates through min, max, range and every coordinate, so
  // one bad reading empties the whole line while leaving an element that still
  // announces itself as a trend. Dropping the unplottable readings draws the
  // ones that are real; dropping all of them draws nothing, which `< 2` below
  // already handles.
  // `slice(-MAX_POINTS)`, not `slice(0, MAX_POINTS)`: the series runs oldest to
  // newest and the accent dot marks the last point as the current period, so
  // keeping the head would draw a stale reading and label it current — quietly
  // wrong in the one place a stat tile is read.
  const plottable = points.filter((value) => Number.isFinite(value)).slice(-MAX_POINTS);
  if (plottable.length < 2) return null;
  const min = Math.min(...plottable);
  const max = Math.max(...plottable);
  const range = max - min || 1;
  const step = (width - pad * 2) / (plottable.length - 1);
  const x = (index: number) => pad + index * step;
  const y = (value: number) => height - pad - ((value - min) / range) * (height - pad * 2);
  const path = plottable
    .map(
      (value, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(value).toFixed(1)}`,
    )
    .join(' ');
  const lastIndex = plottable.length - 1;
  const lastValue = plottable[lastIndex]!;
  return (
    <svg
      className="rp-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      <path
        d={path}
        fill="none"
        stroke={sparklineColors.history}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={x(lastIndex)} cy={y(lastValue)} r={5} fill="var(--rp-surface-card)" />
      <circle cx={x(lastIndex)} cy={y(lastValue)} r={4} fill={sparklineColors.accent} />
    </svg>
  );
}
